import { Box, Chip } from "@mui/material";
import { ImageCheckVerdictEnum } from "../../enums/ImageCheckVerdictEnum";
import { ImageProcessingResponse } from "../../interfaces/ImageProcessing/ImageProcessingResponse";

const normaliseVerdict = (verdict: string) => verdict.trim().toLowerCase();

/**
 * One verdict for the whole payout from its checked images, with the same precedence the backend
 * auto-process uses: any non_compliant wins, then any uncertain (or unknown), else compliant.
 * The reason lists the reasons of the images that carry that verdict.
 */
export const summariseImageCheck = (
  rows?: ImageProcessingResponse[],
): { verdict: string; reason: string } | null => {
  const scored = (rows ?? []).filter((row) => !!row?.uniformVerdict);
  if (!scored.length) return null;
  const verdicts = scored.map((row) => normaliseVerdict(row.uniformVerdict as string));
  const verdict = verdicts.includes(ImageCheckVerdictEnum.NON_COMPLIANT)
    ? ImageCheckVerdictEnum.NON_COMPLIANT
    : verdicts.find((v) => v !== ImageCheckVerdictEnum.COMPLIANT) ?? ImageCheckVerdictEnum.COMPLIANT;
  const reason = scored
    .filter((row) => normaliseVerdict(row.uniformVerdict as string) === verdict)
    .map((row) => row.uniformReason?.trim())
    .filter((r): r is string => !!r)
    .join("; ");
  return { verdict, reason };
};

const verdictColor = (verdict: string) => {
  switch (normaliseVerdict(verdict)) {
    case ImageCheckVerdictEnum.COMPLIANT:
      return "success";
    case ImageCheckVerdictEnum.NON_COMPLIANT:
      return "error";
    case ImageCheckVerdictEnum.UNCERTAIN:
      return "warning";
    default:
      return "default";
  }
};

interface ImageCheckVerdictProps {
  verdict?: string | null;
  reason?: string | null;
  align?: "left" | "center";
}

const ImageCheckVerdict = ({ verdict, reason, align = "center" }: ImageCheckVerdictProps) => {
  if (!verdict) return null;
  return (
    <Box sx={{ maxWidth: align === "center" ? 180 : "unset", textAlign: align }}>
      <Chip
        size="small"
        label={verdict.replace(/_/g, " ").toUpperCase()}
        color={verdictColor(verdict)}
      />
      {reason && (
        <Box sx={{ mt: 0.5 }}>
          <strong style={{ fontSize: "0.75rem", color: "#666" }}>Image Verdict Reason:</strong>
          <div style={{ fontSize: "0.8rem", wordBreak: "break-word" }}>
            {reason}
          </div>
        </Box>
      )}
    </Box>
  );
};

export default ImageCheckVerdict;
